import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from 'components/ui/carousel';
import { Button } from 'components/ui/button';

import BannerImg from 'assets/images/dashboard/banner-1.png';
import { cn } from 'lib/utils';
import useAuthStore from 'store/auth';
import HSBCCardVerificationDialog from './HSBCCardVerificationDialog';

import HSBCLogo from 'assets/hsbc-logo.png';

const BANNER_GRADIENT =
  'linear-gradient(269.95deg, #0DB7A5 -9.46%, #1A68D8 111.33%)';

type WelcomeBannerItem = {
  id: 'welcome';
};

type HsbcMexicoBannerItem = {
  id: 'hsbc-mexico';
  title: string;
  desc: string;
  buttonLabel: string;
};

export type BannerItem = WelcomeBannerItem | HsbcMexicoBannerItem;


export default function Banner({
  isApproved,
}: {
  isApproved: boolean;
}) {
  const { t } = useTranslation();
  const userAttributes = useAuthStore((store) => store.userAttributes);


  function buildBannerItems(options: {
    showHsbcMexicoCardBanner?: boolean;
  }): BannerItem[] {
    const items: BannerItem[] = [{ id: 'welcome' }];

    if (options.showHsbcMexicoCardBanner) {
      items.push({
        id: 'hsbc-mexico',
        title: t('hsbc_card_verification_title'),
        desc:
          t('hsbc_card_verification_info'),
        buttonLabel: t('hsbc_card_verification_button_label'),
      });
    }

    return items;
  }

  const bannerItems = buildBannerItems({ showHsbcMexicoCardBanner: false });
  const [api, setApi] = useState<any>(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [hsbcDialogOpen, setHsbcDialogOpen] = useState(false);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => setCurrent(api.selectedScrollSnap());
    setCount(api.scrollSnapList().length);
    onSelect();

    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api.off('reInit', onSelect);
      api.off('select', onSelect);
    };
  }, [api]);

  useEffect(() => {
    if (!api) return;
    const total = api.scrollSnapList().length;
    if (total <= 1) return;
    if (isHovered) return;

    const interval = window.setInterval(() => {
      const idx = api.selectedScrollSnap();
      if (idx >= total - 1) {
        api.scrollTo(0);
      } else {
        api.scrollNext();
      }
    }, 3000);

    return () => window.clearInterval(interval);
  }, [api, isHovered]);

  return (
    <>
      <Carousel
        className='relative w-full h-full'
        setApi={setApi}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <CarouselContent className='h-full -ml-4'>
          {bannerItems.map((item) => (
            <CarouselItem
              key={item.id}
              className='h-full pl-4 basis-full'
            >
              <div
                className={cn(
                  'relative overflow-hidden rounded-2xl h-full px-5 py-3 sm:px-6 sm:py-3.5',
                )}
                style={{
                  background:
                    item.id === 'hsbc-mexico' ? '#0D8988' : BANNER_GRADIENT,
                }}
              >
                {item.id === 'welcome' ? (
                  <div className='relative flex flex-col justify-center h-full'>
                    <div className='max-w-xs space-y-2 text-white'>
                      {isApproved ? (
                        <h1 className='text-lg'>
                          {t('hello')}{' '}
                          <span className='font-semibold'>
                            {userAttributes?.given_name}
                          </span>
                          ,
                        </h1>
                      ) : (
                        <h1 className='text-lg opacity-80'>
                          {t('verification_in_progress')} ..
                        </h1>
                      )}
                      <p className='w-1/2 text-sm leading-none sm:text-base sm:w-full'>
                        {t('have_a_nice_day')}
                      </p>
                    </div>
                    <div className='absolute z-10 right-2 top-1 sm:right-4 sm:top-2'>
                      <div className='h-24 sm:h-28 w-fit'>
                        <img
                          src={BannerImg}
                          alt='banner image'
                          className='object-contain object-right w-full h-full'
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className='flex flex-row items-center justify-between h-full gap-3 sm:gap-4'>
                    <div className='flex-1 min-w-0 pr-2 space-y-1 text-white'>
                      <div className='flex items-center min-w-0 gap-2'>
                        <img
                          src={HSBCLogo}
                          alt='HSBC'
                          className='w-auto h-5 shrink-0'
                        />
                        <h2 className='min-w-0 text-base font-semibold leading-tight truncate sm:text-lg'>
                          {item.title}
                        </h2>
                      </div>
                      <p
                        className='text-xs leading-tight whitespace-pre-line opacity-95 line-clamp-2 sm:text-sm sm:leading-snug'
                        title={item.desc}
                      >
                        {item.desc}
                      </p>
                    </div>
                    <div className='shrink-0'>
                      <Button
                        type='button'
                        variant='secondary'
                        size='sm'
                        className='h-8 px-3 text-sm whitespace-nowrap sm:h-9 sm:px-4 sm:text-sm bg-[#222B45] hover:bg-[#222B45]/80'
                        onClick={() => setHsbcDialogOpen(true)}
                      >
                        {item.buttonLabel}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>

        {count > 1 ? (
          <div className='absolute flex items-center gap-2 -translate-x-1/2 left-1/2 bottom-2'>
            {Array.from({ length: count }).map((_, idx) => (
              <button
                key={idx}
                type='button'
                aria-label={`Go to slide ${idx + 1}`}
                onClick={() => api?.scrollTo(idx)}
                className={cn(
                  'h-2 w-2 rounded-full transition-all',
                  idx === current ? 'bg-white/95 w-5' : 'bg-white/50 hover:bg-white/70'
                )}
              />
            ))}
          </div>
        ) : null}


      </Carousel>
      <HSBCCardVerificationDialog
        open={hsbcDialogOpen}
        onOpenChange={setHsbcDialogOpen}
      />
    </>
  );
}
